/**
 * Star rating display. Renders 5 stars with proportional fill using CSS
 * clip-path for smooth partial-star rendering.
 *
 * Portal-specific UI component — not a Phaser scene widget, so it lives in the
 * portal, not @genieforge/game-common.
 */

interface RatingStarsProps {
	rating: number;
	size?: "sm" | "md";
}

const STAR_PATH =
	"M10 1l2.47 5.01L18 6.94l-4 3.9.94 5.5L10 13.47l-4.94 2.87.94-5.5-4-3.9 5.53-.93z";

export function RatingStars({ rating, size = "sm" }: RatingStarsProps) {
	const sizeClass = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";

	return (
		<div
			className="flex items-center gap-px"
			role="img"
			aria-label={`${rating.toFixed(1)} out of 5 stars`}
		>
			{[1, 2, 3, 4, 5].map((star) => {
				const fillPct = Math.min(
					100,
					Math.max(0, (rating - star + 1) * 100),
				);
				return (
					<span
						key={star}
						className={`${sizeClass} relative shrink-0 block`}
					>
						{/* Empty star background */}
						<svg
							viewBox="0 0 20 20"
							className="absolute inset-0 w-full h-full"
							aria-hidden="true"
						>
							<path
								d={STAR_PATH}
								fill="var(--color-ink-faint)"
								opacity="0.2"
							/>
						</svg>
						{/* Filled star — clipped for partial fill */}
						{fillPct > 0 && (
							<svg
								viewBox="0 0 20 20"
								className="absolute inset-0 w-full h-full"
								style={
									fillPct < 100
										? {
												clipPath: `inset(0 ${100 - fillPct}% 0 0)`,
											}
										: undefined
								}
								aria-hidden="true"
							>
								<path d={STAR_PATH} fill="var(--color-gold)" />
							</svg>
						)}
					</span>
				);
			})}
		</div>
	);
}
